import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom'; 
import RouteForAdmin from '../components/Routes/RouteForAdmin';
import { InstructionsContainerA, H1, H3 } from './Pages-styles';
import { ChevronRightRegular, ChevronDownRegular } from '@fluentui/react-icons';
import UserBlocking from '../components/Routes/UserBlocking';
import CreateUserModal from '../components/modals/CreateUserModal';
import { usePageContext } from '../hooks/PageContext';
import ActivityInfoModal from '../components/modals/ActivityInfoModal';
import {
  SearchBox,
  ChoiceGroup,
  DetailsList,
  IColumn,
  SelectionMode,
  Persona,
  PersonaSize,
  DefaultButton,
  TooltipHost,
  Stack,
  Modal,
  TextField,
  Dropdown,
  PrimaryButton,
  MessageBar,
  MessageBarType,
  ContextualMenu, // ✅ Для меню с тремя точками
  DirectionalHint,
} from '@fluentui/react';
import UserHistoryModal from '../components/modals/UserHistoryModal';

const Admin: React.FC = () => {
  const ctx = usePageContext();
  const actions = ctx.actions;
  const navigate = useNavigate(); // ✅ Инициализация navigate

  const wheelRef = (node: HTMLDivElement | null) => {
    if (node) {
      const handleWheel = (e: WheelEvent) => {
        e.preventDefault(); // Гарантированно блокирует вертикальную прокрутку
        node.scrollLeft += e.deltaY; // Прокручивает только горизонтально
      };
      
      node.addEventListener('wheel', handleWheel, { passive: false });
      
      // React вызовет эту функцию очистки, когда div будет удалён из DOM
      return () => {
        node.removeEventListener('wheel', handleWheel);
      };
    }
  };

  // Состояние для контекстного меню (три точки)
  const [menuProps, setMenuProps] = useState<{
    target: Element | null;
    items: any[];
    hidden: boolean;
  } | null>(null);
  
  const [selectedUserForMenu, setSelectedUserForMenu] = useState<any>(null);

  // === Типизация для MessageBarType ===
  const getMessageBarType = (type: string): MessageBarType => {
    switch (type) {
      case 'success': return MessageBarType.success;
      case 'error': return MessageBarType.error;
      case 'info': return MessageBarType.info;
      case 'warning': return MessageBarType.warning;
      default: return MessageBarType.info;
    }
  };

  const columns: IColumn[] = [
    {
      key: 'user',
      name: 'Пользователь',
      minWidth: 200,
      maxWidth: 250,
      onRender: (user: any) => (
        <div
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(`/account/edit/${user.id}`)} // ✅ Теперь работает
        >
          <Persona
            text={user.username}
            size={PersonaSize.size32}
            imageUrl={user.avatar}
            imageInitials={user.username[0].toUpperCase()}
            styles={{
              primaryText: {
                color: user.isBlocked ? '#a4262c' : 'inherit',
                textDecoration: user.isBlocked ? 'line-through' : 'none'
              }
            }}
          />
        </div>
      ),
    },
    {
      key: 'email',
      name: 'Email',
      minWidth: 200,
      maxWidth: 250,
      onRender: (user: any) => (
        <div
          style={{
            fontSize: 12,
            color: user.isBlocked ? '#a4262c' : 'inherit',
            textDecoration: user.isBlocked ? 'line-through' : 'none',
          }}
        >
          <div>{user.email} </div>
          <div>{user.phone}</div>
        </div>
      ),
    },
    {
      key: 'company',
      name: 'Компания',
      minWidth: 150,
      maxWidth: 200,
      onRender: (user: any) => (
        <div
          style={{
            fontSize: 12,
            color: user.isBlocked ? '#a4262c' : 'inherit',
            textDecoration: user.isBlocked ? 'line-through' : 'none',
          }}
        >
          {user.company || '-'}
        </div>
      ),
    },
    {
      key: 'access',
      name: 'Доступ',
      minWidth: 100, // Увеличили для информации о выдавшем
      maxWidth: 350,
      onRender: (user: any) => {
        if (user.isBlocked) {
          return (
            <div style={{ fontSize: 12, color: '#a4262c', fontWeight: 'bold' }}>
              Заблокирован
            </div>
          );
        }
        if (user.role === 'superadmin') {
          return <div style={{ fontSize: 12, color: '#107c10' }}>Права управляющего</div>;
        }
        if (user.role === 'admin') {
          return <div style={{ fontSize: 12, color: '#107c10' }}>Права администратора</div>;
        }
        const hasAccess = user.accessExpiry && new Date(user.accessExpiry) > new Date();
        return hasAccess ? (
          <Stack tokens={{ childrenGap: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>
              До: {new Date(user.accessExpiry!).toLocaleDateString()}
            </div>
            {user.reason && (
              <TooltipHost content={user.reason}>
                <div style={{ fontSize: 12, color: '#605e5c' }}>
                  {user.reason.length > 20 ? `${user.reason.substring(0, 20)}...` : user.reason}
                </div>
              </TooltipHost>
            )}
          </Stack>
        ) : (
          <div style={{ fontSize: 12, color: '#a4262c' }}>Нет доступа</div>
        );
      },
    },
    {
      key: 'activity',
      name: 'Активность',
      minWidth: 110,
      maxWidth: 150,
      onRender: (user: any) => {
        const patterns: Record<string, { color: string; label: string }> = {
          'выходной': { color: '#d83b01', label: 'Выходной' },
          'нон-стоп': { color: '#a4262c', label: 'Нон-стоп' },
          'стандарт': { color: '#107c10', label: 'Стандарт' },
          'периодический': { color: '#0078d4', label: 'Периодический' },
          'спящий': { color: '#605e5c', label: 'Спящий' },
          'неопределено': { color: '#a19f9d', label: '—' }
        };

        // ✅ Безопасное получение паттерна с fallback
        const pattern = patterns[user.activityPattern as string] || patterns['неопределено'];

        return (
          <span style={{ 
            fontSize: 12, 
            color: pattern.color, 
            fontWeight: 600 
          }}>
            {pattern.label}
          </span>
        );
      },
    },
    {
      key: 'grantedBy',
      name: 'Выдал доступ',
      minWidth: 120, // Уменьшили ширину
      maxWidth: 150,
      onRender: (user: any) => {
        const hasActiveAccess = user.accessExpiry && new Date(user.accessExpiry) > new Date();
        
        if (user.role === 'user' && hasActiveAccess && user.grantedBy) {
          return (
            <TooltipHost
              content={
                <div style={{ padding: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{user.grantedBy.username}</div>
                  <div style={{ fontSize: 12 }}>{user.grantedBy.email}</div>
                  {user.grantedBy.phone && <div style={{ fontSize: 12 }}>{user.grantedBy.phone}</div>}
                </div>
              }
              directionalHint={DirectionalHint.topCenter}
            >
              <div
                style={{
                  fontSize: 12,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  lineHeight: 1.4,
                }}
                onClick={() => navigate(`/account/edit/${user.grantedBy.id}?view=true`)}
              >
                <span style={{ color: '#605e5c' }}>Доступ выдал(а):</span>
                <span style={{ color: '#0078d4', textDecoration: 'underline', fontWeight: 600 }}>
                  {user.grantedBy.username}
                </span>
              </div>
            </TooltipHost>
          );
        }
        
        return <span style={{ fontSize: 12, color: '#a4262c' }}>-</span>;
      },
    },
    {
      key: 'actions',
      name: 'Действия',
      minWidth: 222,
      onRender: (user: any) => {
        const hasAccess = user.accessExpiry && new Date(user.accessExpiry) > new Date();
        
        // Логика видимости из вашего компонента UserBlocking, чтобы не добавлять пустой пункт в меню
        const canBlock = ['admin', 'superadmin'].includes(actions.currentUserRole) && user.role === 'user' && !user.isBlocked;
        const canUnblock = ['admin', 'superadmin'].includes(actions.currentUserRole) && user.isBlocked;

        return (
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            {/* Кнопка Открыть/Закрыть доступ */}
            {user.role === 'user' && !user.isBlocked && (
              <DefaultButton
                onClick={() => hasAccess 
                  ? actions.handleRevokeAccessOnly(user.email)
                  : actions.openAccessModal(user)
                }
                styles={{ root: { height: 26, minWidth: 120 } }}
              >
                {hasAccess ? 'Закрыть доступ' : 'Открыть доступ'}
              </DefaultButton>
            )}

            {/* Кнопка с тремя точками (меню) */}
            <DefaultButton
              menuIconProps={{ iconName: 'MoreVertical' }}
              onClick={(ev) => {
                setSelectedUserForMenu(user);
                setMenuProps({
                  target: ev.target as Element,
                  hidden: false,
                  items: [
                    {
                      key: 'history',
                      text: 'История',
                      iconProps: { iconName: 'History' },
                      onClick: () => {
                        actions.openHistoryModal(user);
                        setMenuProps(null);
                      }
                    },
                    ...(user.role === 'user' && !user.isBlocked ? [{
                      key: 'makeAdmin',
                      text: 'Сделать админом',
                      iconProps: { iconName: 'UserGauge' },
                      onClick: () => {
                        actions.handleMakeAdmin(user.email);
                        setMenuProps(null);
                      }
                    }] : []),

                      ...(user.role === 'admin' && !user.isBlocked ? [
                      {
                        key: 'revokeAdmin',
                        text: 'Убрать права',
                        iconProps: { iconName: 'UserRemove' },
                        onClick: () => {
                          actions.handleRevokeAccess(user.email);
                          setMenuProps(null);
                        }
                      },
                      ...(actions.currentUserRole === 'superadmin' ? [{
                        key: 'makeSuperadmin',
                        text: 'Сделать суперадмином',
                        iconProps: { iconName: 'Crown' }, // Или 'Admin'
                        onClick: () => {
                          actions.handleMakeSuperadmin(user.email);
                          setMenuProps(null);
                        }
                      }] : [])
                    ] : []),

                    // ✅ Используем ваш компонент, но в режиме "пункта меню"
                    ...((canBlock || canUnblock) ? [{
                      key: 'blocking',
                      // Текст здесь не важен, так как его полностью перекроет onRender, 
                      // но он нужен для валидации типов Fluent UI
                      text: canUnblock ? 'Разблокировать' : 'Заблокировать', 
                      onRender: () => (
                        <UserBlocking
                          user={user}
                          currentUserRole={actions.currentUserRole}
                          refetchUsers={actions.refetchUsers}
                          blockUser={actions.blockUser}
                          unblockUser={actions.unblockUser}
                          asMenuItem={true} // ✅ Включаем режим слияния с меню
                          onActionComplete={() => setMenuProps(null)} 
                        />
                      )
                    }] : [])
                  ]
                });
              }}
              styles={{ root: { height: 26, minWidth: 32 } }}
            />

            {/* Контекстное меню */}
            {menuProps && !menuProps.hidden && selectedUserForMenu?.id === user.id && (
              <ContextualMenu
                {...menuProps}
                onDismiss={() => setMenuProps(null)}
                directionalHint={DirectionalHint.bottomLeftEdge}
                gapSpace={0}
              />
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <InstructionsContainerA>
    <RouteForAdmin>
      {actions.notification && (
        <MessageBar
          messageBarType={getMessageBarType(actions.notification.type)} // ✅ Исправлено
          onDismiss={() => actions.setNotification(null)}
          styles={{ root: { marginBottom: 20 } }}
        >
          {actions.notification.message}
        </MessageBar>
      )}
      <H1>Администрирование</H1>
      <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { marginBottom: 20 } }}>
        <SearchBox
          placeholder="Поиск по имени, email или компании"
          value={actions.searchTerm}
          onChange={(e, newValue) => actions.setSearchTerm(newValue || '')}
          styles={{ root: { width: 300 } }}
        />
        <DefaultButton
          onClick={() => actions.setIsCreateUserModalOpen(true)}
          styles={{ root: { height: 32 } }}
        >
          Создать пользователя
        </DefaultButton>
        <DefaultButton
          iconProps={{ iconName: 'Info' }}
          text="О статусе активности"
          onClick={() => actions.setIsActivityInfoModalOpen(true)}
          styles={{ root: { marginBottom: 16, height: 32 } }}
        />
      </Stack>
      <ChoiceGroup
        label="Сортировать по:"
        options={[
          { key: 'username', text: 'По алфавиту' },
          { key: 'accessExpiry', text: 'По сроку подписки' },
          { key: 'activity', text: 'По активности' }, 
        ]}
        selectedKey={actions.sortOption}
        onChange={(e, option) => option && actions.setSortOption(option.key as 'username' | 'accessExpiry')}
      />
      {/* Суперадмины */}
      {actions.superadmins.length > 0 && (
        <>
          <H3 onClick={() => actions.setIsSuperadminCollapsed(!actions.isSuperadminCollapsed)} style={{ cursor: 'pointer' }}>
            <span style={{ color: '#0078d4', marginRight: '10px' }}>
              {actions.isSuperadminCollapsed ? <ChevronRightRegular /> : <ChevronDownRegular />}
            </span>
            Суперадмины ({actions.superadmins.length})
          </H3>
          {!actions.isSuperadminCollapsed && (
            <div 
              ref={wheelRef}
              style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', marginBottom: 20 }}
            >
              <DetailsList
                key={actions.listKey}
                items={actions.superadmins}
                columns={columns}
                selectionMode={SelectionMode.none}
                compact
                styles={{
                  root: {
                    minWidth: 'max-content',
                    selectors: {
                      '.ms-DetailsRow': { height: 52, alignItems: 'center' },
                      '.ms-DetailsRow-cell': {
                        display: 'flex',
                        alignItems: 'center',
                        paddingTop: 0,
                        paddingBottom: 0,
                      },
                    },
                  },
                  headerWrapper: { display: 'none' },
                  contentWrapper: { width: '100%' },
                }}
              />
            </div>
          )}
        </>
      )}
      {/* Админы */}
      {actions.admins.length > 0 && (
        <>
          <H3 onClick={() => actions.setIsAdminsCollapsed(!actions.isAdminsCollapsed)} style={{ cursor: 'pointer' }}>
            <span style={{ color: '#0078d4', marginRight: '10px' }}>
              {actions.isAdminsCollapsed ? <ChevronRightRegular /> : <ChevronDownRegular />}
            </span>
            Админы ({actions.admins.length})
          </H3>
          {!actions.isAdminsCollapsed && (
            <div 
              ref={wheelRef}
              style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', marginBottom: 20 }}
            >
              <DetailsList
                items={actions.admins}
                columns={columns}
                selectionMode={SelectionMode.none}
                compact
                styles={{
                  root: {
                    minWidth: 'max-content',
                    selectors: {
                      '.ms-DetailsRow': { height: 52, alignItems: 'center' },
                      '.ms-DetailsRow-cell': {
                        display: 'flex',
                        alignItems: 'center',
                        paddingTop: 0,
                        paddingBottom: 0,
                      },
                    },
                  },
                  headerWrapper: { display: 'none' },
                  contentWrapper: { width: '100%' },
                }}
              />
            </div>
          )}
        </>
      )}
      {/* Пользователи */}
      {actions.regularUsers.length > 0 && (
        <>
          <H3 onClick={() => actions.setIsUsersCollapsed(!actions.isUsersCollapsed)} style={{ cursor: 'pointer' }}>
            <span style={{ color: '#0078d4', marginRight: '10px' }}>
              {actions.isUsersCollapsed ? <ChevronRightRegular /> : <ChevronDownRegular />}
            </span>
            Пользователи ({actions.regularUsers.length})
          </H3>
          {!actions.isUsersCollapsed && (
            <div 
              ref={wheelRef}
              style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', marginBottom: 20 }}
            >
              <DetailsList
                items={actions.regularUsers}
                columns={columns}
                selectionMode={SelectionMode.none}
                compact
                styles={{
                  root: {
                    minWidth: 'max-content',
                    selectors: {
                      '.ms-DetailsRow': { height: 52, alignItems: 'center' },
                      '.ms-DetailsRow-cell': {
                        display: 'flex',
                        alignItems: 'center',
                        paddingTop: 0,
                        paddingBottom: 0,
                      },
                    },
                  },
                  headerWrapper: { display: 'none' },
                  contentWrapper: { width: '100%' },
                }}
              />
            </div>
          )}
        </>
      )}
      {/* Модальное окно для открытия доступа */}
      <Modal
        isOpen={actions.accessModal.isOpen}
        onDismiss={actions.closeAccessModal}
        isBlocking={false}
        styles={{
          main: {
            width: 400,
            maxWidth: '100%',
            padding: 20,
            borderRadius: 8,
          },
        }}
      >
        <h3>Открыть доступ {actions.accessModal.user?.username}</h3>
        <TextField
          label= 'Дата окончания доступа:'
          type="date"
          required
          value={actions.accessModal.expiryDate}
          onChange={(_, val) => actions.setAccessModal({ ...actions.accessModal, expiryDate: val || '' })}
        />
        <Dropdown
          label= 'Причина:'
          options={[
            { key: 'оплата', text: 'Оплата' },
            { key: 'демонстрация', text: 'Демонстрация' },
            { key: 'наш сотрудник', text: 'Наш сотрудник' },
          ]}
          required
          selectedKey={actions.accessModal.reason}
          onChange={(_, option) =>
            actions.setAccessModal({ ...actions.accessModal, reason: option?.key as string })
          }
        />
        <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { marginTop: 20 } }}>
          <PrimaryButton onClick={actions.handleGrantAccess}>Сохранить</PrimaryButton>
          <DefaultButton onClick={actions.closeAccessModal}>Отмена</DefaultButton>
        </Stack>
      </Modal>
      {/* Модальное окно создания пользователя */}
      <CreateUserModal
        isOpen={actions.isCreateUserModalOpen}
        onDismiss={() => actions.setIsCreateUserModalOpen(false)}
        currentUserRole={actions.currentUserRole}
        refetchUsers={actions.refetchUsers}
      />
      {/* Модальное окно истории */}
      <UserHistoryModal
        isOpen={actions.historyModal.isOpen}
        onDismiss={actions.closeHistoryModal}
        user={actions.historyModal.user}
      />
      {/* Модальное окно с информацией об активности */}
      <ActivityInfoModal
        isOpen={actions.isActivityInfoModalOpen}
        onClose={() => actions.setIsActivityInfoModalOpen(false)}
      />
    </RouteForAdmin>
    </InstructionsContainerA>
  );
};

export default Admin;